import MDBox from "components/MDBox";
import UiSideModal from "components/base/UiSideModal";
import UiTypography from "components/base/UiTypography";
import { Proposal, ProposalStage } from "generated/graphql";
import { Divider } from "@mui/material";
import PageTour, { PageTourHandle } from "components/PageTour/PageTour";
import TourHelpButton from "components/PageTour/TourHelpButton";
import { useRef } from "react";
import { draftColors } from "modules/proposals/draft/draftDesignTokens";
import SelectProductForm from "modules/products/forms/SelectProductForm";
import useAddProductDrawerActions from "./useAddProductDrawerActions";
import WorksheetProductSection from "./WorksheetProductSection";
import addProductDrawerTourSteps from "./AddProductDrawer.tour";

type AddProductDrawerProps = {
  open: boolean;
  onClose: () => void;
  stage: ProposalStage;
  onSuccess: (proposal: Proposal) => void;
};

export default function AddProductDrawer({
  open,
  onClose,
  stage,
  onSuccess,
}: AddProductDrawerProps) {
  const { handleOnSubmit, insertWorksheetItem, loading, creatingWorksheetItem } =
    useAddProductDrawerActions({ stage, onSuccess, onClose });
  const tourRef = useRef<PageTourHandle>(null);

  return (
    <UiSideModal
      open={open}
      onClose={onClose}
      title="Add Product"
      width="598px"
      closeButtonClassName="tour-add-product-drawer-close"
    >
      <PageTour ref={tourRef} tourKey="addProductDrawer" steps={addProductDrawerTourSteps} />
      <MDBox p="24px" display="flex" flexDirection="column" gap="16px">
        <MDBox display="flex" alignItems="center" gap={0.5}>
          <UiTypography uiVariant="heading-sm">Product</UiTypography>
          <TourHelpButton
            tooltip="Click here to learn more about adding a Product"
            onClick={() => tourRef.current?.replay()}
          />
        </MDBox>
        <SelectProductForm
          onSubmit={handleOnSubmit}
          loading={loading}
          mapData={stage.mapData}
          hideSelectionLabel
          useNewDesign
        />

        <Divider sx={{ borderColor: draftColors.border }} />

        <WorksheetProductSection
          open={open}
          disabled={creatingWorksheetItem}
          onAddBlank={() => insertWorksheetItem()}
          onSelect={insertWorksheetItem}
        />
      </MDBox>
    </UiSideModal>
  );
}
